import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatChip } from "@angular/material/chips";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { CapabilityRegistry, layerHandoffCapabilities } from "../../../../services/capability.registry";
import { FeatureFlagsService } from "../../../../../../viewer-components/services/internal-services/feature-flags.service";
import { ActionService } from "../../../../services/action.service";
import { DebugModeService } from "../../../../services/debug-mode.service";
import { FileUploadService } from "../../../../services/file-upload.service";
import { CardHeaderComponent } from "../../shared/card-header/card-header.component";
import { TypeChipComponent, type ChipType } from "../../shared/type-chip/type-chip.component";
import { ContentDataViewerComponent } from "../../shared/content-data-viewer/content-data-viewer.component";
import { ContentActionBarComponent } from "../../shared/content-action-bar/content-action-bar.component";
import { contentFormatToResultType, mimeToContentFormat, GEO_CONTENT_KINDS } from "../../../../model/block.types";
import type { ContentBlockRef, ContentBlockKind, ContentBlockMetadata, ContentBlockData } from "../../../../model/block.types";
import type { DataResult, ActionContext, DataContentType } from "../../../../model/data.types";
import type { ActionId, Capability } from "../../../../model/action.types";

/** Files below this threshold are fetched automatically; larger ones require an explicit "Load" click. */
const AUTO_LOAD_THRESHOLD = 500 * 1024; // 500 KB

type FetchState = "idle" | "loading" | "loaded" | "error";

/**
 * Reusable card for rendering a FileBlock produced when use_file_store=True.
 * Maps mime_type to the appropriate render strategy and delegates display to existing
 * ContentDataViewerComponent + ContentActionBarComponent.
 *
 * Can be used standalone in any context (MCP tool cards, AI messages, etc.).
 */
@Component({
  selector: "app-file-block-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    MatCard,
    MatCardContent,
    MatChip,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    CardHeaderComponent,
    TypeChipComponent,
    ContentDataViewerComponent,
    ContentActionBarComponent,
  ],
  templateUrl: "./file-block-card.component.html",
  styleUrl: "./file-block-card.component.scss",
})
export class FileBlockCardComponent implements OnDestroy {
  ref = input.required<ContentBlockRef>();

  /** Used to pick geotiff/geojson-aware capabilities the same way mime_type-based inference did before. */
  kind = input<ContentBlockKind | undefined>(undefined);

  /** `ContentBlockData.metadata` — used for `zoom_to`/`move_to`/feature-count on geo content kinds (GEO_CONTENT_KINDS). */
  metadata = input<ContentBlockMetadata>(null);

  /**
   * The owning block's full data — optional (standalone/generic usages omit it). When present,
   * it is handed to ActionService in the action context so `add_layer` can route contract
   * blocks (`data.layer`) through EvidenceLayerService (group + typed style + name).
   */
  block = input<ContentBlockData | null>(null);

  /** When true, suppresses the outer mat-card wrapper and header so this component can be embedded inside another card. */
  embedded = input<boolean>(false);

  /** When embedded=true, adds a top border to visually separate this content from a params section above. */
  borderTop = input<boolean>(false);

  /**
   * Optional contextual override for the `add_layer` capability label (map-layer-block-ui S11):
   * the map-layer card passes "Add layer"/"Update layer" so the standard bar carries the one
   * contextual affordance. Unset (every existing usage) → byte-identical registry labels.
   */
  addLayerLabel = input<string | undefined>(undefined);

  private readonly fileUploadService = inject(FileUploadService);

  private readonly capabilityRegistry = inject(CapabilityRegistry);

  private readonly featureFlags = inject(FeatureFlagsService);

  private readonly actions = inject(ActionService);

  protected readonly debugMode = inject(DebugModeService);

  private readonly destroy$ = new Subject<void>();

  protected readonly fetchState = signal<FetchState>("idle");

  protected readonly loadedResult = signal<DataResult | null>(null);

  protected readonly fetchError = signal<string | null>(null);

  /** Resolved img src for image blocks: fetched data URL from preview_url, or query-param-auth main URL. */
  protected readonly thumbnailSrc = signal<string | null>(null);

  protected readonly resultType = computed(() => contentFormatToResultType(mimeToContentFormat(this.ref().mime_type)));

  protected readonly isImage = computed(() => this.resultType() === "image");

  /** Isolated URL signals so the thumbnail effect only re-fires when URLs actually change. */
  private readonly _previewUrl = computed(() => this.ref().preview_url);

  private readonly _blockUrl = computed(() => this.ref().url);

  /** True for types where content must be fetched from the ref's URL before rendering. */
  protected readonly needsFetch = computed(() => {
    const t = this.resultType();
    return t === "geojson" || t === "json" || t === "text" || t === "geotiff";
  });

  /** GeoTIFF is never auto-loaded (tends to be large). Others auto-load if under threshold. */
  protected readonly shouldAutoLoad = computed(() => {
    if (!this.needsFetch() || this.resultType() === "geotiff") return false;
    return (this.ref().size_bytes ?? Infinity) < AUTO_LOAD_THRESHOLD;
  });

  protected readonly chipType = computed((): ChipType => {
    const t = this.resultType();
    if (t === "image") return "image";
    if (t === "geojson") return "geojson";
    if (t === "json") return "json";
    if (t === "text") return "text";
    return "binary";
  });

  /** DataResult for types that need no fetch (image, pdf, binary). Null for fetch-required types. */
  protected readonly immediateResult = computed((): DataResult | null => {
    if (this.needsFetch()) return null;
    const ref = this.ref();
    return {
      type: this.resultType() as DataContentType,
      url: this.fileUploadService.authenticatedUrl(ref.url),
      filename: ref.filename,
      sizeBytes: ref.size_bytes ?? undefined,
    };
  });

  protected readonly capabilities = computed(() => {
    let all = this.capabilityRegistry.getAllCapabilities(this.resultType() as DataContentType);
    if (this.resultType() === "geojson") {
      const dr = this.loadedResult();
      if (!this.actions.hasExtractableGeometry(dr?.geojson)) {
        all = all.filter((c) => c.action !== "zoom_to");
      }
    }
    const mapped = this.#mapCapabilitiesForContentKind(all);
    // S11 label override: only the add_layer entry changes, and only when the input is set.
    const addLayerLabel = this.addLayerLabel();
    const withLabel =
      addLayerLabel === undefined ? mapped : mapped.map((c) => (c.action === "add_layer" ? { ...c, label: addLayerLabel } : c));
    // Viewer-handoff (F1): contract blocks with ref gain "Open in Embeddings"/"Open in Elements".
    return layerHandoffCapabilities(this.block(), this.featureFlags, withLabel);
  });

  /** GeoJSON feature count chip, restored for geo content kinds — mirrors ToolDataCardComponent's inline-value counterpart. */
  protected readonly geojsonFeatureCount = computed((): number | null => {
    const dr = this.loadedResult() ?? this.immediateResult();
    if (!dr || dr.type !== "geojson" || !dr.geojson) return null;
    const g = dr.geojson as Record<string, unknown>;
    if (g.type === "FeatureCollection" && Array.isArray(g.features)) {
      return (g.features as unknown[]).length;
    }
    return null;
  });

  protected readonly actionContext = computed((): ActionContext => {
    const ref = this.ref();
    const loaded = this.loadedResult();
    const url = this.fileUploadService.authenticatedUrl(ref.url);
    const ctx: ActionContext = { url, filename: ref.filename };
    // Hand the owning block to ActionService when available: contract blocks route through
    // EvidenceLayerService (plan group + typed style), plain adds keep the legacy path.
    const ownerBlock = this.block();
    if (ownerBlock) ctx.block = ownerBlock;
    // GeoTIFF is never auto-loaded (large files) and add_layer only needs the URL, not the
    // decoded content — fall back to a minimal stub so "Add layer" works before "Load" is clicked.
    const geotiffStub: DataResult | null = this.resultType() === "geotiff" ? { type: "geotiff", url, filename: ref.filename } : null;
    const dr = loaded ?? this.immediateResult() ?? geotiffStub;
    if (dr) {
      ctx.dataResult = dr;
      if (dr.geojson) ctx.geometry = dr.geojson;
      if (dr.text) ctx.textContent = dr.text;
    }

    const kind = this.kind();
    const metadata = this.metadata();
    if (kind && GEO_CONTENT_KINDS.has(kind) && metadata) {
      const bbox = metadata["bbox"] as [number, number, number, number] | undefined;
      if (bbox) ctx.bounds = bbox;
      const center = metadata["center"] as [number, number] | undefined;
      if (center) ctx.point = { lng: center[0], lat: center[1], zoom: metadata["zoom_hint"] as number | undefined };
    }
    return ctx;
  });

  protected readonly sizeLabel = computed(() => {
    const b = this.ref().size_bytes;
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  });

  constructor() {
    // Auto-fetch small non-geotiff files
    effect(() => {
      if (this.shouldAutoLoad() && this.fetchState() === "idle") {
        this.loadContent();
      }
    });

    // Resolve thumbnail for image blocks.
    // Reads only _previewUrl/_blockUrl (primitives) so Angular skips re-runs when other block
    // fields change during streaming. authenticatedUrl() reads headers() — wrap in untracked()
    // to prevent the headers signal from becoming an effect dependency.
    effect(() => {
      if (!this.isImage()) return;
      const previewUrl = this._previewUrl();
      const blockUrl = this._blockUrl();
      if (previewUrl) {
        this.fileUploadService
          .fetchPreviewDataUrl(previewUrl)
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe({
            next: (dataUrl) => this.thumbnailSrc.set(dataUrl),
            // Fallback to auth-decorated main URL if preview fetch fails
            error: () => untracked(() => this.thumbnailSrc.set(this.fileUploadService.authenticatedUrl(blockUrl))),
          });
      } else {
        untracked(() => this.thumbnailSrc.set(this.fileUploadService.authenticatedUrl(blockUrl)));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected loadContent(): void {
    if (this.fetchState() === "loading") return;
    this.fetchState.set("loading");
    const { url, mime_type, filename, size_bytes } = this.ref();
    this.fileUploadService
      .fetchFileContent(url, mime_type, { filename, size_bytes })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.loadedResult.set(result);
          this.fetchState.set("loaded");
        },
        error: (err) => {
          console.error("FileBlockCardComponent: load failed", err);
          this.fetchError.set("Failed to load file content");
          this.fetchState.set("error");
        },
      });
  }

  protected onAction(action: ActionId): void {
    this.actions.execute(action, this.actionContext());
  }

  /** Adds zoom_to/move_to quick actions (backed by metadata.bbox/metadata.center) for geo content kinds. */
  #mapCapabilitiesForContentKind(base: Capability[]): Capability[] {
    const kind = this.kind();
    const metadata = this.metadata();
    if (!kind || !GEO_CONTENT_KINDS.has(kind) || !metadata) return base;

    const extras: Capability[] = [];
    if (metadata["bbox"] && !base.some((c) => c.action === "zoom_to")) {
      extras.push({ action: "zoom_to", label: "Zoom to", icon: "zoom_out_map", primary: base.length === 0 });
    }
    if (metadata["center"] && !base.some((c) => c.action === "move_to")) {
      extras.push({ action: "move_to", label: "Center", icon: "my_location" });
    }
    return extras.length > 0 ? [...base, ...extras] : base;
  }
}
